import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiUploadCloud } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { CATEGORY_GET, CATEGORY_CREATE } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import MultiSelect from '../MultiSelect';
import './Page.css';
import './CreateCategory.css';

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const AGE_GROUP_OPTIONS = [
  { value: '0-3', label: '0-3' },
  { value: '3-6', label: '3-6' },
  { value: '6+', label: '6+' },
];

function CreateCategory() {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateId = location.state?.duplicateId;
  const [name, setName] = useState('');
  const [status, setStatus] = useState('Active');
  const [age_groups, setAge_groups] = useState([]);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(!!duplicateId);

  useEffect(() => {
    if (!duplicateId) return;
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(CATEGORY_GET.pathFor(duplicateId)), { method: CATEGORY_GET.method, headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Parent category not found.' : `Request failed (${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json.data || json;
        setName(data.name ?? '');
        setStatus(data.status === 'Inactive' ? 'Inactive' : 'Active');
        setAge_groups(Array.isArray(data.age_groups) ? data.age_groups : []);
        if (data.thumbnailUrl) setThumbnailPreview(data.thumbnailUrl);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load category to duplicate.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDuplicate(false);
      });
    return () => { cancelled = true; };
  }, [duplicateId]);

  useEffect(() => {
    return () => {
      if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) URL.revokeObjectURL(thumbnailPreview);
    };
  }, [thumbnailPreview]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setError('');
    if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) {
      URL.revokeObjectURL(thumbnailPreview);
      setThumbnailPreview(null);
    }
    if (!file) {
      setThumbnailFile(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (e.g. JPG, PNG).');
      setThumbnailFile(null);
      return;
    }
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Parent category name is required.');
      return;
    }
    if (!thumbnailFile) {
      setError('Please upload a thumbnail image.');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const formData = new FormData();
      formData.append('name', trimmedName);
      formData.append('status', status);
      age_groups.forEach((v) => formData.append('age_groups', v));
      formData.append('thumbnail', thumbnailFile);

      const res = await fetch(apiUrl(CATEGORY_CREATE.path), {
        method: CATEGORY_CREATE.method,
        headers,
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setIsSubmitting(false);
        return;
      }

      navigate('/dashboard/cms/category');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page create-category-page">
      <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/cms/category')}>
        <FiArrowLeft className="back-icon" aria-hidden />
        Back to Parent categories
      </button>

      <div className="create-category-header">
        <h3>{duplicateId ? 'Duplicate parent category' : 'Create parent category'}</h3>
        <p className="create-category-subtitle">
          {duplicateId
            ? 'Edit details below and upload a new thumbnail to save as a new parent category.'
            : 'Add a new category with name, thumbnail and status.'}
        </p>
      </div>

      {loadingDuplicate && <p className="create-category-loading">Loading category…</p>}

      <form onSubmit={handleSubmit} className="create-category-form" style={loadingDuplicate ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
        <div className="form-page-layout">
          <div className="form-section">
            <h4 className="form-section-title">Details</h4>
            <div className="form-grid-2">
              <div className="form-row-single">
                <label htmlFor="category-name">Parent category name <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="category-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Nursery Rhymes"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="category-status">Status</label>
                <select
                  id="category-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={isSubmitting}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single form-row-full">
                <label>Age groups</label>
                <MultiSelect
                  options={AGE_GROUP_OPTIONS}
                  value={age_groups}
                  onChange={setAge_groups}
                  placeholder="Select age groups"
                  disabled={isSubmitting}
                  searchable={false}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">Thumbnail <span className="label-required" aria-hidden="true">*</span></h4>
            <div className="form-thumbnail-with-preview">
              <div className="thumbnail-upload-area">
                <input
                  type="file"
                  id="thumbnail-upload"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                  className="thumbnail-input"
                />
                <label htmlFor="thumbnail-upload" className="thumbnail-upload-label">
                  <FiUploadCloud className="upload-icon" aria-hidden />
                  <span>{thumbnailFile ? thumbnailFile.name : 'Choose image to upload'}</span>
                </label>
              </div>
              {thumbnailPreview && (
                <div className="thumbnail-preview-wrap">
                  <img src={thumbnailPreview} alt="Thumbnail preview" className="thumbnail-preview-img" />
                </div>
              )}
            </div>
          </div>
        </div>

        {error && <div className="create-category-error" role="alert">{error}</div>}

        <div className="form-actions form-actions-full">
          <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard/cms/category')} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create parent category'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateCategory;
